import React, { useState, ReactNode } from 'react';
import ReactToPrint from 'react-to-print';
import { Button, Checkbox, FormGroup, FormControlLabel, IconButton, Menu } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Print } from '@mui/icons-material';
import makeStyles from '@mui/styles/makeStyles';
import i18next from 'i18next';
import { morgengrauen } from '@/config/colors';
import { useSettings } from '../Settings/SettingsContext';

interface PrintWrapperProps {
  setCols: (cols: string[]) => void;
  children: ReactNode;
  activeCols: string[];
  setDimension: (dimension: string) => void;
  interactiveToggle?: ReactNode;
}

const useStyles = makeStyles(() => ({
  label: {
    fontSize: 14,
    margin: '0',
  },
  printButton: {
    position: 'absolute',
    top: '13px',
    right: '60px',
    height: '40px',
    border: '1px solid #ef5927',
    padding: '6px 8px',
  },
  printButtonText: {
    fontSize: '14px',
    color: '#ef5927',
    marginLeft: '10px',
  },
  togglebutton: {
    position: 'absolute',
    top: '13px',
    left: '106px'
  },
  buttonclr: {
    color: '#ef5927'
  },
  menuPaper: {
    height: 'auto',
    width: 'auto',
    minWidth: 220,
    maxWidth: 360,
  },
  formControl: {
    margin: '0px'
  },
  formGroup: {
    padding: '10px',
  },
  checkboxLabel: {
    fontSize: 14,
    marginLeft: 8,
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
    lineHeight: 1.25,
  },
}));

const PrintWrapper: React.FC<PrintWrapperProps> = ({ setCols, children, activeCols, interactiveToggle }) => {
  const [cols, setColumns] = useState(activeCols);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const classes = useStyles();

  const { settings } = useSettings();
  const dimensions = settings.load_dimensions || [];

  const loadCols = dimensions
    .map((d: any) => ({
      backendIdx: d.backendIndex,
      label: d.dimension,
      unit: d.unit
    }))
    .filter((d: any) => typeof d.backendIdx === 'number');

  const loadKey = (backendIdx: number) => `load_${backendIdx}`;

  // Toggle column visibility
  const toggleColumn = (name: string) => {
    const newCols = [...cols];
    if (newCols.includes(name)) {
      const i = newCols.findIndex(item => item === name);
      newCols.splice(i, 1);
    } else {
      newCols.push(name);
    }
    setColumns(newCols);
    setCols(newCols);
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const menuOpen = Boolean(anchorEl);

  return (
    <>
      <div style={{ fontFamily: 'Roboto', fontSize: '16px' }}>
        <style>
          {`
            @media print {
              .hidden-print { display: none !important; }
              html, body {
                height: 100% !important;
                overflow: visible !important;
              }
              body * {
                visibility: hidden;
              }
              #print-content, #print-content * {
                visibility: visible;
                overflow: visible !important;
              }
              #print-content {
                position: absolute;
                left: 0;
                top: 0;
                width: 100%;
              }
            }
          `}
        </style>

        {/* Interactive Toggle — rendered when feature flag is active */}
        {interactiveToggle && (
          <div className="hidden-print">
            {interactiveToggle}
          </div>
        )}

        {/* Print Button */}
        <ReactToPrint
          trigger={() => (
            <Button
              className={classes.printButton}
              size="large"
              data-testid="print-button"
              aria-label="print"
            >
              <Print color={'primary'} style={{ color: morgengrauen }} />
              <span className={classes.printButtonText}>{i18next.t('common:export.print')}</span>
            </Button>
          )}
          content={() => document.getElementById('print-content')}
        />

        {/* Menu Toggle */}
        <IconButton
          onClick={handleClick}
          className={classes.togglebutton}
          data-testid="column-menu-toggle"
          aria-haspopup="menu"
          aria-controls="column-menu"
          aria-expanded={menuOpen ? 'true' : 'false'}
        >
          <MoreVertIcon className={classes.buttonclr} />
        </IconButton>

        {/* Columns Menu */}
        <Menu
          id="column-menu"
          anchorEl={anchorEl}
          open={menuOpen}
          onClose={handleClose}
          data-testid="column-menu"
          slotProps={{
            paper: {
              sx: {
                width: 'auto',
                minWidth: 220,
                maxWidth: 360,
                maxHeight: 480,
              },
            },
          }}
        >
          <FormGroup className={classes.formGroup} >
            <FormControlLabel
              classes={{ label: classes.checkboxLabel }}
              className={classes.formControl}
              control={
                <Checkbox
                  checked={cols.includes('id')}
                  style={{ color: morgengrauen }}
                  onChange={() => toggleColumn('id')}
                  data-testid="col-id"
                />
              }
              label={i18next.t('common:export.id')}
            />

            <FormControlLabel
              classes={{ label: classes.checkboxLabel }}
              className={classes.formControl}
              control={
                <Checkbox
                  style={{ color: morgengrauen }}
                  checked={cols.includes('contactcompany')}
                  onChange={() => toggleColumn('contactcompany')}
                  data-testid="col-contactcompany"
                />
              }
              label={i18next.t('common:export.contact')}
            />

            <FormControlLabel
              classes={{ label: classes.checkboxLabel }}
              className={classes.formControl}
              control={
                <Checkbox
                  style={{ color: morgengrauen }}
                  checked={cols.includes('city')}
                  onChange={() => toggleColumn('city')}
                  data-testid="col-city"
                />
              }
              label={i18next.t('common:export.city')}
            />

            <FormControlLabel
              classes={{ label: classes.checkboxLabel }}
              className={classes.formControl}
              control={
                <Checkbox
                  style={{ color: morgengrauen }}
                  checked={cols.includes('address')}
                  onChange={() => toggleColumn('address')}
                  data-testid="col-address"
                />
              }
              label={i18next.t('common:export.address')}
            />

            <FormControlLabel
              classes={{ label: classes.checkboxLabel }}
              className={classes.formControl}
              control={
                <Checkbox
                  style={{ color: morgengrauen }}
                  checked={cols.includes('eta')}
                  onChange={() => toggleColumn('eta')}
                  data-testid="col-eta"
                />
              }
              label={i18next.t('common:export.timewindow')}
            />

            <FormControlLabel
              classes={{ label: classes.checkboxLabel }}
              className={classes.formControl}
              control={
                <Checkbox
                  style={{ color: morgengrauen }}
                  checked={cols.includes('time_windows')}
                  onChange={() => toggleColumn('time_windows')}
                  data-testid="col-time_windows"
                />
              }
              label={i18next.t('common:routes.timeWindow')}
            />

            <FormControlLabel
              classes={{ label: classes.checkboxLabel }}
              className={classes.formControl}
              control={
                <Checkbox
                  style={{ color: morgengrauen }}
                  checked={cols.includes('duration')}
                  onChange={() => toggleColumn('duration')}
                  data-testid="col-duration"
                />
              }
              label={i18next.t('common:export.duration')}
            />

            {/* Dynamic load-dimension checkboxes */}
            {loadCols.map(({ backendIdx, label, unit }) => {
              const key = loadKey(backendIdx);
              return (
                <FormControlLabel
                  key={key}
                  classes={{ label: classes.checkboxLabel }}
                  className={classes.formControl}
                  control={
                    <Checkbox
                      style={{ color: morgengrauen }}
                      checked={cols.includes(key)}
                      onChange={() => toggleColumn(key)}
                      data-testid={`col-${key}`}
                    />
                  }
                  label={`${label}${unit ? ` (${unit})` : ''}`}
                />
              );
            })}

            <FormControlLabel
              classes={{ label: classes.checkboxLabel }}
              className={classes.formControl}
              control={
                <Checkbox
                  style={{ color: morgengrauen }}
                  checked={cols.includes('area')}
                  onChange={() => toggleColumn('area')}
                  data-testid="col-area"
                />
              }
              label={i18next.t('common:export.area')}
            />

            <FormControlLabel
              classes={{ label: classes.checkboxLabel }}
              className={classes.formControl}
              control={
                <Checkbox
                  style={{ color: morgengrauen }}
                  checked={cols.includes('shipment_type')}
                  onChange={() => toggleColumn('shipment_type')}
                  data-testid="col-shipment_type"
                />
              }
              label={i18next.t('common:export.shipmentType')}
            />
          </FormGroup>
        </Menu>

        {/* Content to be printed */}
        <div id="print-content" data-testid="print-content">
          {children}
        </div>
      </div>
    </>
  );
};

export default PrintWrapper;
